import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toJpeg, toBlob } from 'html-to-image'
// Directo, no por el barrel de ui: el barrel arrastra react-router (SidebarMenu) y eso
// no carga en Jest sin router.
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { partirRol, validarRol, renderRolFragmento } from '../../rol/rol-template'
import { verificarRecortes } from '../../rol/verificar'

// El rol de jornada como imagen para WhatsApp y redes, con la plantilla de src/rol
// (SPEC.md): lienzo fijo segun campos x horarios, JPEG calidad 85, validaciones antes
// de pintar y verificacion de recortes antes de exportar. Todo en el navegador.
//
// Recibe el rol YA armado ({ rol, clubes }), o varios con `roles` ([{ rol, clubes, sede }], uno
// por sede, de armarRolesPorSede): el torneo interno lo arma
// src/rol/mapeoRolInterno.js (desde `juegos`) y la participacion en la red lo arma
// src/rol/mapeoRol.js (desde get_rol_jornada). El modal no sabe de que mundo viene.

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap'
const ensureFont = () => {
  if (typeof document === 'undefined') return
  if (!document.querySelector('link[data-rol-font]')) {
    const l = document.createElement('link')
    l.rel = 'stylesheet'; l.href = FONT_HREF; l.setAttribute('data-rol-font', '1')
    document.head.appendChild(l)
  }
}

const urlToDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors' })
    const blob = await res.blob()
    return await new Promise((resolve) => {
      const fr = new FileReader()
      fr.onload = () => resolve(fr.result)
      fr.onerror = () => resolve(null)
      fr.readAsDataURL(blob)
    })
  } catch { return null }
}

// rol-j07-20260911-1840.jpg (o rol-20260913-20260911-1840.jpg si no hay jornada): la fecha
// y hora de generacion en el nombre evitan que circule un rol viejo. Con varias sedes
// el nombre lleva la sede (rol-j07-20260911-1840-miami-soccer.jpg).
export const nombreArchivo = (rol, generadoEn, parte, partes, sede) => {
  const d = new Date(generadoEn)
  const p = (n) => String(n).padStart(2, '0')
  const sello = Number.isNaN(d.getTime()) ? 'sin-fecha'
    : `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`
  const base = rol?.jornada ? `j${p(rol.jornada)}` : String(rol?.fecha || 'juegos').replace(/-/g, '')
  const suf = partes > 1 ? `-parte${parte}` : ''
  const lugar = sede ? `-${String(sede).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` : ''
  return `rol-${base}-${sello}${lugar}${suf}.jpg`
}

// Un solo bloque: preview escalado + copia a tamano real fuera de pantalla (target de captura).
// El html viene de renderRolFragmento, que escapa con esc() todo dato de la base (nombres,
// categorias, ids); los colores salen de la paleta propia. No entra texto sin escapar.
function Bloque({ fragmento, previewWidth, innerRef }) {
  const scale = Math.min(0.5, previewWidth / fragmento.w)
  const marca = { __html: fragmento.html }
  return (
    <>
      <style>{fragmento.css}</style>
      <div className="overflow-auto rounded-md border">
        <div style={{ width: fragmento.w * scale, height: fragmento.h * scale }}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: fragmento.w, height: fragmento.h }}
            dangerouslySetInnerHTML={marca} />
        </div>
      </div>
      <div style={{ position: 'fixed', left: -99999, top: 0, width: fragmento.w, height: fragmento.h }} aria-hidden>
        <div ref={innerRef} style={{ width: fragmento.w, height: fragmento.h }} dangerouslySetInnerHTML={marca} />
      </div>
    </>
  )
}

export default function CompartirRolModal({ open, onOpenChange, rol: rolUnico, clubes: clubesUnico, roles, fondoUrl, titulo }) {
  const nodeRef = useRef(null)
  const [escudos, setEscudos] = useState({})
  const [fondo, setFondo] = useState(null)
  const [logo, setLogo] = useState(null)
  const [conFondo, setConFondo] = useState(true)
  const [parte, setParte] = useState(0)
  const [sedeIdx, setSedeIdx] = useState(0)
  const [generando, setGenerando] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [generadoEn, setGeneradoEn] = useState(() => new Date().toISOString())
  const canShare = typeof navigator !== 'undefined' && !!navigator.canShare

  useEffect(() => {
    if (!open) { setEscudos({}); setFondo(null); setLogo(null); setConFondo(true); setParte(0); setSedeIdx(0); setAviso(null); return }
    ensureFont()
    setGeneradoEn(new Date().toISOString())
  }, [open])

  // Un rol por sede (`roles`) o el rol unico de siempre.
  const armadosTodos = useMemo(
    () => (Array.isArray(roles) && roles.length ? roles : [{ rol: rolUnico, clubes: clubesUnico }]),
    [roles, rolUnico, clubesUnico]
  )
  // Una sede sin partidos exportables (sin hora o con rival por definir) no tiene imagen: fuera del selector.
  // Si ninguna tiene, se dejan todas y sale el mensaje de vacio.
  const armados = useMemo(() => {
    const conPartidos = armadosTodos.filter((a) => a.rol?.partidos?.length > 0)
    return conPartidos.length ? conPartidos : armadosTodos
  }, [armadosTodos])
  const variasSedes = armados.length > 1
  const idx = Math.min(sedeIdx, armados.length - 1)
  const armado = armados[idx]
  const rol = armado.rol
  const clubesPorSede = useMemo(() => armados.map((a) => a.clubes || {}), [armados])
  const clubesBase = clubesPorSede[idx]

  // Logo de la liga/torneo embebido (mismo motivo que los escudos); sin logo no se pinta.
  const logoUrl = armados[0].rol?.logo_url
  useEffect(() => {
    if (!open || !logoUrl) { setLogo(null); return undefined }
    let cancel = false
    urlToDataUrl(logoUrl).then((d) => { if (!cancel) setLogo(d) })
    return () => { cancel = true }
  }, [open, logoUrl])

  const rolSellado = useMemo(() => (rol ? { ...rol, generado_en: generadoEn, logo_url: logo || null } : null), [rol, generadoEn, logo])
  // Cada sede se valida aparte: un error en una bloquea solo su imagen.
  const erroresPorSede = useMemo(
    () => armados.map((a, i) => (a.rol?.partidos?.length ? validarRol({ ...a.rol, generado_en: generadoEn, logo_url: logo || null }, clubesPorSede[i]) : [])),
    [armados, clubesPorSede, generadoEn, logo]
  )
  const errores = erroresPorSede[idx] || []

  // Escudos y fondo embebidos: html-to-image no puede leer una imagen remota sin CORS.
  useEffect(() => {
    if (!open) return undefined
    let cancel = false
    const urls = Array.from(new Set(clubesPorSede.flatMap((cs) => Object.values(cs)).map((c) => c.escudo_url).filter(Boolean)))
    Promise.all(urls.map(async (u) => [u, await urlToDataUrl(u)])).then((pares) => {
      if (!cancel) setEscudos(Object.fromEntries(pares.filter(([, d]) => d)))
    })
    return () => { cancel = true }
  }, [open, clubesPorSede])

  useEffect(() => {
    if (!open || !fondoUrl) { setFondo(null); return undefined }
    let cancel = false
    urlToDataUrl(fondoUrl).then((d) => { if (!cancel) setFondo(d) })
    return () => { cancel = true }
  }, [open, fondoUrl])

  const clubesEmbebidos = useMemo(() => Object.fromEntries(
    Object.entries(clubesBase).map(([id, c]) => [id, { ...c, escudo_url: c.escudo_url ? (escudos[c.escudo_url] || undefined) : undefined }])
  ), [clubesBase, escudos])

  const bloques = useMemo(() => {
    if (!rolSellado?.partidos?.length || errores.length) return []
    try {
      const opciones = conFondo && fondo ? { fondoUrl: fondo } : {}
      return partirRol(rolSellado).map((b) => ({ rol: b, fragmento: renderRolFragmento(b, clubesEmbebidos, opciones) }))
    } catch (e) {
      return [{ error: e.message }]
    }
  }, [rolSellado, clubesEmbebidos, errores.length, conFondo, fondo])

  const actual = bloques[parte] || bloques[0] || null
  const vacio = armados.every((a) => !a.rol?.partidos?.length)
  const fallaLienzo = actual?.error

  const capturar = async (fn) => {
    if (!nodeRef.current || !actual?.fragmento) return
    setGenerando(true); setAviso(null)
    try {
      if (document.fonts?.ready) await document.fonts.ready
      const v = verificarRecortes(nodeRef.current)
      if (v.total > 0) {
        setAviso(`La imagen no se exporta: ${v.desbordes} texto(s) desbordan la tarjeta y ${v.nombresCortados} nombre(s) no caben. Acorta el nombre o define el nombre corto del equipo.`)
        return
      }
      const { w, h } = actual.fragmento
      await fn(nodeRef.current, { quality: 0.85, pixelRatio: 1, width: w, height: h, cacheBust: true, type: 'image/jpeg' })
    } catch (e) {
      setAviso(e?.message || 'No se pudo generar la imagen.')
    } finally { setGenerando(false) }
  }

  const archivo = () => nombreArchivo(rolSellado, generadoEn, actual?.rol?.parte, actual?.rol?.partes, variasSedes ? armado.sede : null)

  const descargar = () => capturar(async (node, opts) => {
    const url = await toJpeg(node, opts)
    const a = document.createElement('a')
    a.href = url; a.download = archivo(); a.click()
  })

  const compartir = () => capturar(async (node, opts) => {
    const blob = await toBlob(node, opts)
    if (!blob) return
    const file = new File([blob], archivo(), { type: 'image/jpeg' })
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: titulo || 'Rol' }) } catch { /* usuario cancelo */ }
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Compartir rol</DialogTitle>
          <DialogDescription>
            {actual?.fragmento ? `Imagen ${actual.fragmento.w} × ${actual.fragmento.h}, JPEG. Lista para WhatsApp.` : 'El rol del día como imagen.'}
          </DialogDescription>
        </DialogHeader>

        {vacio ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No hay juegos con horario para compartir.</p>
        ) : (
          <div className="space-y-2">
            {variasSedes && (
              <div className="flex flex-wrap gap-1" role="group" aria-label="Imagen por sede">
                {armados.map((a, i) => (
                  <Button key={a.sede || i} size="sm" variant={i === idx ? 'default' : 'outline'}
                    onClick={() => { setSedeIdx(i); setParte(0); setAviso(null) }}>
                    {a.sede}
                  </Button>
                ))}
              </div>
            )}
            {errores.length > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p className="mb-2 font-medium text-destructive">El rol no se genera hasta corregir esto:</p>
                <ul className="list-disc space-y-1 pl-5">
                  {errores.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            ) : fallaLienzo ? (
              <p className="rounded-md border p-3 text-sm text-destructive">{fallaLienzo}</p>
            ) : actual ? (
              <>
            {bloques.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {bloques.map((b, i) => (
                  <Button key={i} size="sm" variant={i === parte ? 'default' : 'outline'} onClick={() => setParte(i)}>
                    Parte {i + 1} de {bloques.length}
                  </Button>
                ))}
              </div>
            )}
            <Bloque key={`${idx}-${parte}-${conFondo && fondo ? 'f' : 's'}`} fragmento={actual.fragmento} previewWidth={540} innerRef={nodeRef} />
            {fondoUrl && (
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4" checked={conFondo} onChange={(e) => setConFondo(e.target.checked)} />
                Usar la imagen de fondo del torneo
              </label>
            )}
              </>
            ) : null}
          </div>
        )}

        {aviso && <p className="text-sm text-destructive">{aviso}</p>}

        {actual?.fragmento && (
          <DialogFooter>
            <Button variant="outline" onClick={descargar} disabled={generando}>
              {generando ? 'Generando...' : 'Descargar JPEG'}
            </Button>
            {canShare && <Button onClick={compartir} disabled={generando}>Compartir</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
